// Data for the project estimation calculator

// Service types with base price ranges
export const serviceTypes = [
  {
    id: "cable-railing",
    name: "Cable Railing",
    description: "Modern, minimalist cable railing systems with unobstructed views",
    basePricePerFoot: 130, // base price per foot in USD
    minPricePerFoot: 100,
    maxPricePerFoot: 350,
  },
  {
    id: "indoor-railing",
    name: "Indoor Railing",
    description: "Custom interior railings that blend with your home decor",
    basePricePerFoot: 120,
    minPricePerFoot: 90,
    maxPricePerFoot: 320,
  },
  {
    id: "ornamental-work",
    name: "Ornamental Work",
    description: "Exquisite ornamental metal work with artistic detailing",
    basePricePerHour: 95, // base price per hour in USD
    minPrice: 750, // minimum project cost
    maxPrice: 25000, // no real upper limit but set for UI purposes
  },
  {
    id: "outdoor-railing",
    name: "Outdoor Railing",
    description: "Durable, weather-resistant railings for decks, patios, and staircases",
    basePricePerFoot: 135,
    minPricePerFoot: 110,
    maxPricePerFoot: 380,
  },
  {
    id: "spiral-stairs",
    name: "Spiral Stairs",
    description: "Space-efficient, visually stunning spiral staircases",
    basePricePerHour: 110, // base price per hour
    minPrice: 4500, // minimum project cost
    maxPrice: 35000, // upper range for luxury spiral staircases
  },
  {
    id: "stainless-steel",
    name: "Stainless Steel",
    description: "Premium stainless steel fabrication for contemporary applications",
    basePricePerHour: 105,
    minPrice: 800,
    maxPrice: 30000,
  },
  {
    id: "steel-fabrication",
    name: "Steel Fabrication",
    description: "Custom steel fabrication for structural and architectural applications",
    basePricePerHour: 90,
    minPrice: 600,
    maxPrice: 40000,
  }
];

// Material types with multipliers (affects the price)
export const materialTypes = [
  {
    id: 'carbon-steel',
    name: 'Carbon Steel',
    description: 'Standard steel, strong and versatile',
    priceMultiplier: 1.0,
    suitableFor: ['cable-railing', 'indoor-railing', 'outdoor-railing', 'spiral-stairs', 'steel-fabrication'],
  },
  {
    id: 'stainless-304',
    name: 'Stainless Steel 304',
    description: 'Standard stainless grade, good corrosion resistance',
    priceMultiplier: 1.5,
    suitableFor: ['cable-railing', 'indoor-railing', 'outdoor-railing', 'spiral-stairs', 'stainless-steel'],
  },
  {
    id: 'stainless-316',
    name: 'Stainless Steel 316',
    description: 'Marine-grade stainless, excellent for outdoor/coastal environments',
    priceMultiplier: 1.8,
    suitableFor: ['cable-railing', 'outdoor-railing', 'stainless-steel'],
  },
  {
    id: 'aluminum',
    name: 'Aluminum',
    description: 'Lightweight and corrosion-resistant',
    priceMultiplier: 1.3,
    suitableFor: ['cable-railing', 'indoor-railing', 'outdoor-railing', 'spiral-stairs'],
  },
  {
    id: 'wrought-iron',
    name: 'Wrought Iron',
    description: 'Traditional look with decorative potential',
    priceMultiplier: 1.4,
    suitableFor: ['indoor-railing', 'outdoor-railing', 'ornamental-work'],
  },
  {
    id: 'mild-steel',
    name: 'Mild Steel',
    description: 'Economical option for indoor applications',
    priceMultiplier: 0.9,
    suitableFor: ['indoor-railing', 'steel-fabrication', 'spiral-stairs'],
  },
  {
    id: 'galvanized-steel',
    name: 'Galvanized Steel',
    description: 'Zinc-coated steel for rust prevention',
    priceMultiplier: 1.2,
    suitableFor: ['outdoor-railing', 'steel-fabrication'],
  },
  {
    id: 'corten-steel',
    name: 'Corten Steel',
    description: 'Weathering steel with distinctive rustic patina',
    priceMultiplier: 1.7,
    suitableFor: ['outdoor-railing', 'ornamental-work', 'steel-fabrication'],
  },
  {
    id: 'copper',
    name: 'Copper',
    description: 'Distinctive appearance with natural patina over time',
    priceMultiplier: 2.0,
    suitableFor: ['indoor-railing', 'ornamental-work'],
  },
  {
    id: 'brass',
    name: 'Brass',
    description: 'Gold-like appearance, good for decorative elements',
    priceMultiplier: 1.8,
    suitableFor: ['indoor-railing', 'ornamental-work'],
  },
  {
    id: 'bronze',
    name: 'Bronze',
    description: 'Rich, warm-toned metal with excellent durability',
    priceMultiplier: 1.9,
    suitableFor: ['indoor-railing', 'ornamental-work'],
  }
];

// Finish types with additional costs
export const finishTypes = [
  {
    id: "raw",
    name: "Raw Metal",
    description: "Natural metal finish with clear protective coating",
    additionalCost: 1.0,
    suitableFor: ["indoor-railing", "stainless-steel", "ornamental-work"]
  },
  {
    id: "paint",
    name: "Paint",
    description: "Standard paint finish in various colors",
    additionalCost: 1.1,
    suitableFor: ["indoor-railing", "outdoor-railing", "spiral-stairs", "steel-fabrication"]
  },
  {
    id: "powder-coat",
    name: "Powder Coat",
    description: "Durable and high-quality finish with excellent weather resistance",
    additionalCost: 1.2,
    suitableFor: ["cable-railing", "indoor-railing", "outdoor-railing", "spiral-stairs", "steel-fabrication"]
  },
  {
    id: "galvanized",
    name: "Galvanized",
    description: "Zinc coating for rust protection, ideal for outdoor applications",
    additionalCost: 1.15,
    suitableFor: ["outdoor-railing", "steel-fabrication"]
  },
  {
    id: "brushed",
    name: "Brushed Finish",
    description: "Elegant brushed metal look with subtle texture",
    additionalCost: 1.25,
    suitableFor: ["cable-railing", "indoor-railing", "stainless-steel"]
  },
  {
    id: "mirror",
    name: "Mirror Polished",
    description: "High-gloss reflective finish, typically for stainless steel",
    additionalCost: 1.4,
    suitableFor: ["cable-railing", "indoor-railing", "stainless-steel"]
  },
  {
    id: "blackened",
    name: "Blackened/Patina",
    description: "Darkened finish for an aged or vintage look",
    additionalCost: 1.3,
    suitableFor: ["indoor-railing", "ornamental-work", "steel-fabrication"]
  },
  {
    id: "oil-rubbed",
    name: "Oil-Rubbed Bronze",
    description: "Dark bronze-like appearance with subtle highlights",
    additionalCost: 1.35,
    suitableFor: ["indoor-railing", "ornamental-work"]
  },
  {
    id: "antiqued",
    name: "Antiqued Finish",
    description: "Deliberately aged appearance with distressed details",
    additionalCost: 1.45,
    suitableFor: ["indoor-railing", "ornamental-work"]
  },
  {
    id: "painted-distressed",
    name: "Painted & Distressed",
    description: "Paint with controlled wear to achieve a vintage look",
    additionalCost: 1.5,
    suitableFor: ["indoor-railing", "ornamental-work"]
  },
  {
    id: "satin",
    name: "Satin Finish",
    description: "Smooth, matte finish that reduces glare and fingerprints",
    additionalCost: 1.2,
    suitableFor: ["cable-railing", "indoor-railing", "stainless-steel"]
  }
];

// Complexity levels with multipliers
export const complexityLevels = [
  {
    id: "simple",
    name: "Simple",
    description: "Basic design with minimal detail and standard components",
    priceMultiplier: 1.0,
    examples: {
      "cable-railing": "Straight cable rail sections with standard post spacing",
      "indoor-railing": "Straight metal railings with simple balusters",
      "outdoor-railing": "Standard straight deck railing",
      "spiral-stairs": "Basic spiral with standard tread design",
      "ornamental-work": "Simple decorative elements with minimal curves",
      "stainless-steel": "Basic stainless components with straight cuts",
      "steel-fabrication": "Simple structural elements with standard connections"
    }
  },
  {
    id: "moderate",
    name: "Moderate",
    description: "Some decorative elements or custom features with varied details",
    priceMultiplier: 1.3,
    examples: {
      "cable-railing": "Multiple rail sections with corners and gate integration",
      "indoor-railing": "Curved sections or mixed material components",
      "outdoor-railing": "Multiple levels or integrated lighting features",
      "spiral-stairs": "Custom tread design or specialized handrail",
      "ornamental-work": "Decorative scrollwork or custom patterns",
      "stainless-steel": "Fabricated components with multiple welds and finishes",
      "steel-fabrication": "Custom shapes requiring multiple processes"
    }
  },
  {
    id: "complex",
    name: "Complex",
    description: "Intricate design with multiple decorative elements and custom features",
    priceMultiplier: 1.6,
    examples: {
      "cable-railing": "Multi-level systems with custom posts and integrated features",
      "indoor-railing": "Unique designs with multiple material integration",
      "outdoor-railing": "Complex curves with ornamental elements",
      "spiral-stairs": "Custom spiral with integrated landings or unique central column",
      "ornamental-work": "Elaborate artistic elements or historical reproductions",
      "stainless-steel": "Multi-component assemblies with precise tolerances",
      "steel-fabrication": "Artistic or specialized structural elements"
    }
  },
  {
    id: "highly-complex",
    name: "Highly Complex",
    description: "Ornate design with extensive custom work and artistic elements",
    priceMultiplier: 2.0,
    examples: {
      "cable-railing": "Architectural feature with compound curves and integrated art",
      "indoor-railing": "Museum-quality installation with unique artistic elements",
      "outdoor-railing": "Sculptural railing system with integrated functional features",
      "spiral-stairs": "Floating design with glass treads or sculptural elements",
      "ornamental-work": "Museum-quality sculptural work with intricate detail",
      "stainless-steel": "Architectural sculpture or water feature components",
      "steel-fabrication": "Monumental or public art requiring engineering certification"
    }
  }
];

// Urgency levels with multipliers
export const urgencyLevels = [
  {
    id: 'standard',
    name: 'Standard',
    description: 'Regular project timeline',
    priceMultiplier: 1.0,
    timeDescription: 'Standard delivery time (4-6 weeks)'
  },
  {
    id: 'priority',
    name: 'Priority',
    description: 'Expedited project with higher priority',
    priceMultiplier: 1.25,
    timeDescription: 'Faster delivery (2-3 weeks)'
  },
  {
    id: 'urgent',
    name: 'Urgent',
    description: 'Rush project with highest priority',
    priceMultiplier: 1.5,
    timeDescription: 'Urgent delivery (1-2 weeks)'
  }
];

// Installation requirements
export const installationTypes = [
  {
    id: 'not-needed',
    name: 'Not Needed',
    description: 'No installation required',
    additionalCost: 0,
  },
  {
    id: 'basic',
    name: 'Basic Installation',
    description: 'Standard installation process',
    additionalCost: 350,
  },
  {
    id: 'complex',
    name: 'Complex Installation',
    description: 'Requires specialized equipment or difficult access',
    additionalCost: 750,
  }
];

// Location factors (distance from Ridgefield, CT)
export const locationFactors = [
  {
    id: 'local',
    name: 'Local (0-15 miles)',
    description: 'Within Ridgefield and nearby towns',
    additionalCost: 0,
  },
  {
    id: 'nearby',
    name: 'Nearby (15-30 miles)',
    description: 'Within 30 miles of Ridgefield',
    additionalCost: 100,
  },
  {
    id: 'regional',
    name: 'Regional (30-60 miles)',
    description: 'Within 60 miles of Ridgefield',
    additionalCost: 200,
  },
  {
    id: 'distant',
    name: 'Distant (60+ miles)',
    description: 'More than 60 miles from Ridgefield',
    additionalCost: 350,
  }
];

// Function to calculate a rough estimate based on provided parameters
export const calculateEstimate = (parameters: {
  serviceType: string;
  material: string;
  finish: string;
  complexity: string;
  urgency: string;
  installation: string;
  location: string;
  size: number;
  additionalDetails?: string;
}) => {
  // Find the selected options
  const service = serviceTypes.find(s => s.id === parameters.serviceType);
  const material = materialTypes.find(m => m.id === parameters.material);
  const finish = finishTypes.find(f => f.id === parameters.finish);
  const complexity = complexityLevels.find(c => c.id === parameters.complexity);
  const urgency = urgencyLevels.find(u => u.id === parameters.urgency);
  const installation = installationTypes.find(i => i.id === parameters.installation);
  const location = locationFactors.find(l => l.id === parameters.location);

  if (!service || !material || !finish || !complexity || !urgency || !installation || !location) {
    return {
      minEstimate: 0,
      maxEstimate: 0,
      error: 'Please select all required parameters'
    };
  }

  let minEstimate = 0;
  let maxEstimate = 0;
  
  // Calculate based on service type
  if ('basePricePerFoot' in service && service.minPricePerFoot && service.maxPricePerFoot) {
    // For services priced per foot (railings, structural)
    minEstimate = service.minPricePerFoot * parameters.size;
    maxEstimate = service.maxPricePerFoot * parameters.size;
  } else if ('basePricePerHour' in service && service.basePricePerHour && service.minPrice && service.maxPrice) {
    // For services priced per project/hour (custom fabrication, repair)
    // Complexity affects the estimate more for these services
    const hourEstimate = Math.ceil(parameters.size * complexity.priceMultiplier);
    minEstimate = Math.max(service.minPrice, hourEstimate * service.basePricePerHour * 0.8);
    maxEstimate = Math.min(service.maxPrice, hourEstimate * service.basePricePerHour * 1.5);
  }

  // Apply multipliers and additional costs
  minEstimate *= material.priceMultiplier;
  maxEstimate *= material.priceMultiplier;
  
  minEstimate *= finish.additionalCost;
  maxEstimate *= finish.additionalCost;
  
  minEstimate *= complexity.priceMultiplier;
  maxEstimate *= complexity.priceMultiplier;
  
  minEstimate *= urgency.priceMultiplier;
  maxEstimate *= urgency.priceMultiplier;
  
  minEstimate += installation.additionalCost;
  maxEstimate += installation.additionalCost;
  
  minEstimate += location.additionalCost;
  maxEstimate += location.additionalCost;

  // Round to nearest $50
  minEstimate = Math.ceil(minEstimate / 50) * 50;
  maxEstimate = Math.ceil(maxEstimate / 50) * 50;

  return {
    minEstimate,
    maxEstimate,
    error: null
  };
};